/**
 * SponsoredCard -- public-platform paid-ad card.
 *
 * Visual = workspace AdBanner exactly (rounded card, full-bleed
 * banner image, small "Ad" label bottom-right). The one addition
 * for public-platform compliance is a tiny (i) info dot top-right
 * that opens the "Why this card?" modal. Source images come from
 * the same 4 banner URLs already in Supabase (800x150, 800x250,
 * 1080x1080, fullscreen) so we get format diversity per surface.
 *
 * - Tracks impressions on mount
 * - Tracks clicks on tap (workspace shared infra)
 * - Hidden for premium (handled by SponsoredSlot)
 */
import React, { useEffect, useRef, useState } from 'react';
import { trackAdClick, trackAdImpression } from '../../workspace/services/advertisingService';
import WhyThisCardModal from './WhyThisCardModal';
import './adBanner.css';

// 2026-09-08: Return both the URL AND the actual banner size that
// was picked, so the container aspect-ratio matches the real asset.
// (Previously we hardcoded aspect ratio from the *requested* size,
// which letterboxed / stretched when the requested size fell back
// to a smaller banner variant on the advertiser record.)
const pickBanner = (ad, size) => {
  if (!ad) return { url: null, actualSize: size };
  switch (size) {
    case 'medium':
      if (ad.banner_800x250_url) return { url: ad.banner_800x250_url, actualSize: 'medium' };
      if (ad.banner_800x150_url) return { url: ad.banner_800x150_url, actualSize: 'small' };
      return { url: null, actualSize: size };
    case 'large':
      if (ad.banner_fullscreen_url) return { url: ad.banner_fullscreen_url, actualSize: 'large' };
      if (ad.banner_1080x1080_url) return { url: ad.banner_1080x1080_url, actualSize: 'square' };
      return { url: null, actualSize: size };
    case 'square':
      if (ad.banner_1080x1080_url) return { url: ad.banner_1080x1080_url, actualSize: 'square' };
      if (ad.banner_800x250_url) return { url: ad.banner_800x250_url, actualSize: 'medium' };
      return { url: null, actualSize: size };
    case 'small':
    default:
      if (ad.banner_800x150_url) return { url: ad.banner_800x150_url, actualSize: 'small' };
      if (ad.banner_800x250_url) return { url: ad.banner_800x250_url, actualSize: 'medium' };
      return { url: null, actualSize: size };
  }
};

const aspectRatioFor = (size) => {
  switch (size) {
    case 'medium': return '800 / 250';
    case 'large':  return '9 / 16';
    case 'square': return '1 / 1';
    case 'small':
    default:       return '800 / 150';
  }
};

export const SponsoredCard = ({
  ad,
  surface,
  size = 'small',
  state,
  className = '',
}) => {
  const [showWhy, setShowWhy] = useState(false);
  const impressionFired = useRef(false);
  // 2026-09-08: Track the actual image aspect ratio so the container
  // tightens to it on load (prevents both crop AND letterbox even if
  // the advertiser asset isn't exactly 9:16 / 1:1 / 800x150 / 800x250).
  const [naturalAspect, setNaturalAspect] = useState(null);

  useEffect(() => {
    if (!ad || impressionFired.current) return;
    impressionFired.current = true;
    trackAdImpression(ad.id);
  }, [ad]);

  if (!ad) return null;
  const { url: imgUrl, actualSize } = pickBanner(ad, size);
  if (!imgUrl) return null;

  const onImgLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.currentTarget || {};
    if (naturalWidth && naturalHeight) {
      setNaturalAspect(`${naturalWidth} / ${naturalHeight}`);
    }
  };

  const onClick = (e) => {
    // Don't navigate if the click came from the (i) info dot.
    if (e.target.closest('[data-bp-why-card]')) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    trackAdClick(ad.id, surface || 'unknown');
    if (ad.redirect_url) {
      window.open(ad.redirect_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <>
      <a
        href={ad.redirect_url || '#'}
        target="_blank"
        rel="noopener noreferrer sponsored"
        onClick={onClick}
        className={`bp-pub-ad ${className}`.trim()}
        style={{ aspectRatio: naturalAspect || aspectRatioFor(actualSize) }}
        data-testid={`sponsored-card-${surface || 'unknown'}`}
        aria-label={`Sponsored: ${ad.name || 'Advertisement'}`}
      >
        <img
          src={imgUrl}
          alt={ad.name || 'Sponsored'}
          loading="lazy"
          decoding="async"
          onLoad={onImgLoad}
          className="bp-pub-ad-img"
        />
        <button
          type="button"
          data-bp-why-card
          aria-label="Why am I seeing this ad?"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowWhy(true);
          }}
          className="bp-pub-ad-info"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4" />
            <path d="M12 8h.01" />
          </svg>
        </button>
        <span className="bp-pub-ad-label">Ad</span>
      </a>

      {showWhy && (
        <WhyThisCardModal
          state={state}
          onClose={() => setShowWhy(false)}
        />
      )}
    </>
  );
};

export default SponsoredCard;
